import React, { useCallback, useEffect, useState } from "react";
import ChatSearch from "./ChatSearch/ChatSearch";
import SingleChatSideBarHeader from "../../../Components/MainChatSideBarHeader/SingleChatSideBarHeader";
import { FadeLoader } from "react-spinners";
import ContactUserList from "../../../Components/AllUserList/ContactUserList";
import UserChatList from "../../../Components/AllUserList/UserChatList";
import {
  decryptMessage,
  decryptText,
} from "../../../Components/EncryptDecryptMessage/EncryptDecryptMessage";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getApiCaller, postApiCaller } from "../../../Libs/apiCaller";
import InfiniteScroll from "../../../Components/InfiniteScroll/InfiniteScroll";
import { useNavigate } from "react-router-dom";

export default function ChatSideBar({
  AllUser,
  chatUsers,
  activeChat,
  setShowAddMenu,
  setActiveChat,
  setCreateChatId,
  setChatConversationId,
  setGetSecretKey,
  chatSearch,
  moreNewChat,
  setMoreNewChat,
  setChatSearch,
  allUserSearch,
  setAllUserSearch,
  handleAllUserSearch,
  isAlluserLoading,
  setChatUsers,
  chatPage,
  setChatPage,
  setActiveSection,
  chatTotalPages,
  setChatTotalPages,
  setIsMessageLoading,
  allUserCurrentPage,
  setAllUserCurrentPage,
  allUserTotalPages,
  fetchAllUser,
  setShowProfileView,
  isNewUserMessage,
  setIsNewUserMessage,
  setShowGlobalSearch,
}) {
  const [allUserChatWithMsg, setallUserChatWithMsg] = useState([]);
  const { userInfo } = useSelector((state) => state.authUser);
  const [isLoading, setIsLoading] = useState(false);
  const [timeOutIdForSearch, setTimeOutIdForSearch] = useState(null);
  const [hasLoadedChatList, setHasLoadedChatList] = useState(false);

  // Global Search State
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearching, setIsGlobalSearching] = useState(false);
  const navigate = useNavigate();

  const getRelativeTime = (date) => {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now - past) / 1000);
    if (diffInSeconds < 60) return "now";
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d`;
    return `${Math.floor(diffInDays / 365)}y`;
  };

  useEffect(() => {
    if (!moreNewChat) {
      setIsLoading(true);
      // Load chat list immediately instead of waiting 700ms
      fetchChatList(1, "");
    }
  }, [moreNewChat]);

  useEffect(() => {
    if (isNewUserMessage) {
      fetchChatList(1, "");
      setIsNewUserMessage(false);
    }
  }, [isNewUserMessage]);

  const fetchChatList = async (page = chatPage, search) => {
    // !showLoading && setIsLoading(true);
    let endpoint = `chat?page=${page}&limit=10${search ? `&search=${search}` : ""
      }`;
    const response = await getApiCaller(endpoint);
    try {
      if (response?.status === 200) {
        setChatUsers((prev) =>
          page == 1 ? response.data.data : [...prev, ...response.data.data]
        );
        setChatPage(response.data?.pagination?.currentPage);
        setChatTotalPages(response.data?.pagination?.totalPages);
      } else {
        toast.error(response?.response?.data?.message || response.message);
      }
    } finally {
      setHasLoadedChatList(true);
      setIsLoading(false);
    }
  };

  const performGlobalSearch = async (query) => {
    if (!query.trim()) {
      setIsGlobalSearching(false);
      setGlobalSearchResults([]);
      fetchChatList(1, "");
      return;
    }

    setIsGlobalSearching(true);
    setIsLoading(true);
    try {
      const response = await postApiCaller("messages/search", {
        query: query,
        page: 1,
        limit: 20,
      });

      if (response.status === 200) {
        setGlobalSearchResults(response.data.data);
      } else {
        setGlobalSearchResults([]);
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setChatSearch(value);
    clearTimeout(timeOutIdForSearch);
    const timeoutID = setTimeout(() => {
      if (value.trim()) {
        performGlobalSearch(value);
      } else {
        setIsGlobalSearching(false);
        fetchChatList(1, "");
      }
    }, 500); // Increased debounce slightly for API
    setTimeOutIdForSearch(timeoutID);
  };

  const handleResultClick = (msg) => {
    // Navigate based on originType
    const originType = msg.conversationId?.originType || msg.originType;
    let path = "/home";
    if (originType === "group") path = "/groups";
    if (originType === "channel") path = "/channels";

    if (msg.entityId) {
      // Pass messageId and highlight flag in state for deep linking
      navigate(`${path}?entityId=${msg.entityId}`, {
        state: {
          activeMessageId: msg.messageId,
          highlightMessage: true
        }
      });
    } else {
      toast.error("Cannot navigate to this conversation");
    }
  };

  const getAllMessages = useCallback(async () => {
    if (!userInfo?.encryptedPrivateKey || !chatUsers?.length) return [];

    // OPTIMIZATION: Decrypt incrementally - show chats immediately, decrypt in background
    // First, return chats with encrypted text (user sees list immediately)
    const initialResults = chatUsers.map((msg) => ({
      ...msg,
      latestMessage: msg.latestMessage ? {
        ...msg.latestMessage,
        cipherText: msg.latestMessage.cipherText || "", // Show encrypted text temporarily
      } : msg.latestMessage,
    }));

    // Then decrypt in background (non-blocking)
    Promise.all(
      chatUsers.map(async (msg) => {
        if (!msg?.latestMessage?.cipherText) return msg;

        try {
          const getSecretKey = await decryptText(
            msg?.conversationId?.AESKeyOfUser,
            userInfo.encryptedPrivateKey
          );

          const decryptedText = await decryptMessage(
            msg.latestMessage.cipherText,
            msg.latestMessage.iv,
            getSecretKey
          );

          return {
            ...msg,
            latestMessage: {
              ...msg.latestMessage,
              cipherText: decryptedText,
            },
          };
        } catch (err) {
          console.error("Decryption failed:", err);
          return {
            ...msg,
            latestMessage: {
              ...msg.latestMessage,
              cipherText: "",
            },
          };
        }
      })
    ).then((decrypted) => {
      // Update with decrypted messages once ready
      const sortedMessages = decrypted.sort((a, b) => {
        const timeA = a.latestMessage?.sentTime || a.sortTime;
        const timeB = b.latestMessage?.sentTime || b.sortTime;
        return new Date(timeB) - new Date(timeA);
      });
      setallUserChatWithMsg(sortedMessages);
    });

    // Return initial results immediately (non-blocking)
    return initialResults;
  }, [chatUsers, userInfo?.encryptedPrivateKey]);

  useEffect(() => {
    // OPTIMIZATION: Show chat list immediately, decrypt in background
    if (!chatUsers?.length) {
      setallUserChatWithMsg([]);
      return;
    }

    // Show chats immediately (with encrypted text if no key yet)
    const initialSorted = [...chatUsers].sort((a, b) => {
      const timeA = a.latestMessage?.sentTime || a.sortTime;
      const timeB = b.latestMessage?.sentTime || b.sortTime;
      return new Date(timeB) - new Date(timeA);
    });
    setallUserChatWithMsg(initialSorted);

    // Decrypt in background if we have the key (non-blocking)
    if (userInfo?.encryptedPrivateKey) {
      getAllMessages().then((decrypted) => {
        if (decrypted && decrypted.length > 0) {
          const sortedMessages = decrypted.sort((a, b) => {
            const timeA = a.latestMessage?.sentTime || a.sortTime;
            const timeB = b.latestMessage?.sentTime || b.sortTime;
            return new Date(timeB) - new Date(timeA);
          });
          setallUserChatWithMsg(sortedMessages);
        }
      });
    }
  }, [getAllMessages, chatUsers, userInfo?.encryptedPrivateKey]); // Add userInfo dependency

  return (
    <section className="w-full">
      <SingleChatSideBarHeader
        moreNewChat={moreNewChat}
        setMoreNewChat={setMoreNewChat}
        setChatSearch={setChatSearch}
        allUserSearch={allUserSearch}
        setAllUserSearch={setAllUserSearch}
        setAllUserCurrentPage={setAllUserCurrentPage}
      />
      <div className="m-4 mb-0">
        {!moreNewChat && (
          <ChatSearch chatSearch={chatSearch} handleSearch={handleSearch} />
        )}
        {moreNewChat && (
          <ChatSearch
            chatSearch={allUserSearch}
            handleSearch={handleAllUserSearch}
          />
        )}
      </div>
      <div className="p-4 pr-2 h-[calc(100vh-130px)] max-h-[calc(100vh-130px)] min-h-[calc(100vh-130px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">

        {moreNewChat && !isAlluserLoading && AllUser?.length > 0 && (
          <InfiniteScroll
            dataLength={AllUser.length} //Use actual array length instead of total count
            next={() => fetchAllUser(allUserCurrentPage + 1, "")}
            hasMore={allUserCurrentPage < allUserTotalPages} // Compare current page with total pages
            loader={<p className="text-center">Loading...</p>}
          >
            {AllUser?.map((item) => (
              <ContactUserList
                key={item?._id || item.id}
                chat={item}
                moreNewChat={moreNewChat}
                setMoreNewChat={setMoreNewChat}
                setActiveChat={setActiveChat}
                setCreateChatId={setCreateChatId}
                setIsMessageLoading={setIsMessageLoading}
                setShowProfileView={setShowProfileView}
              />
            ))}
          </InfiniteScroll>
        )}

        {moreNewChat && !isAlluserLoading && AllUser.length <= 0 && (
          <div className="h-[calc(100vh-190px)] flex items-center justify-center">
            <p className="text-[#7104F8] font-semibold text-xl">
              User Not Found
            </p>
          </div>
        )}

        {/* Global Search Results Render */}
        {isGlobalSearching && (
          <div className="flex flex-col gap-2">
            {globalSearchResults.map((msg) => (
              <div
                key={msg._id}
                onClick={() => handleResultClick(msg)}
                className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
              >
                <img
                  src={msg.senderId?.userImage || "/default_user.png"}
                  alt={msg.senderId?.name}
                  className="w-10 h-10 rounded-full object-cover min-w-[40px]"
                  onError={(e) => { e.target.src = "/default_user.png" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-0.5">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate max-w-[120px]">
                      {msg.senderId?.name || "Unknown"}
                    </h4>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                      {getRelativeTime(msg.sentTime)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 break-all">
                    {msg.content}
                  </p>
                  <span className="text-[9px] px-1 py-0.5 rounded bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-300 mt-1 inline-block">
                    {msg.conversationId?.originType || "Chat"}
                  </span>
                </div>
              </div>
            ))}

            {globalSearchResults.length === 0 && !isLoading && (
              <div className="text-center py-8 text-gray-500 text-sm">
                No messages found.
              </div>
            )}
          </div>
        )}

        {!isLoading &&
          !isAlluserLoading &&
          !moreNewChat &&
          !isGlobalSearching && // Only show normal list if NOT searching globally
          (allUserChatWithMsg.length > 0 || chatUsers.length > 0) && (
            <InfiniteScroll
              dataLength={
                allUserChatWithMsg.length || chatUsers.length
              } //This is important field to render the next data
              next={() => fetchChatList(chatPage + 1)}
              hasMore={chatTotalPages > chatPage}
              loader={<p className="text-center">Loading...</p>}
            >
              {(allUserChatWithMsg.length > 0
                ? allUserChatWithMsg
                : chatUsers
              ).map((item) => (
                <UserChatList
                  setShowAddMenu={setShowAddMenu}
                  key={item._id || item.conversationId?._id}
                  chat={item}
                  activeChat={activeChat}
                  setActiveChat={setActiveChat}
                  setCreateChatId={setCreateChatId}
                  setChatConversationId={setChatConversationId}
                  setGetSecretKey={setGetSecretKey}
                  setActiveSection={setActiveSection}
                  setIsMessageLoading={setIsMessageLoading}
                  setShowProfileView={setShowProfileView}
                />
              ))}
            </InfiniteScroll>
          )}
        {!isLoading &&
          !isAlluserLoading &&
          !moreNewChat &&
          !isGlobalSearching && // Ensure we don't show "Chat Not Found" when searching globally
          hasLoadedChatList &&
          allUserChatWithMsg.length <= 0 && (
            <div className="min-h-[calc(100dvh-170px)] content-center justify-items-center">
              <p className="text-[#7104F8] font-semibold text-xl mb-16">
                Chat Not Found
              </p>
            </div>
          )}
      </div>
    </section>
  );
}
